import { defineStore } from "pinia";
import { ref } from "vue";
import { type Comment, type Comments, Message, type Messages } from "@/model";
import { ServerApiAccess } from "@/server-api-access";
import { useAppStore } from "./app-store";
import type { UpdatePayload as CommentUpdatePayload } from "./comment-store";
import { useCommentStore } from "./comment-store";
import { useFileStore } from "./file-store";
import MessageDao from "./message-dao";

type DateRange = { from: string | null; to: string | null };

type Payload = { message: Message };
type PayloadAdd = {
  domainId: string;
  topicId: string;
  messages: Message[];
};
type PayloadFetch = {
  domainId: string;
  topicId: string;
  messageId?: string;
};
export type PayloadRegisterSubscription = { domainId: string; topicId: string };

export const useMessageStore = defineStore("messages", () => {
  const messages = ref<Messages>({});
  const nextToken = ref<Record<string, string | null | undefined>>({});
  const range = ref<Record<string, DateRange>>({});

  const findTopicMessages = (domainId: string, topicId: string): Message[] => {
    if (messages.value[domainId] === undefined) {
      messages.value = { ...messages.value, [domainId]: {} };
    }

    if (messages.value[domainId][topicId] === undefined) {
      const topicMessages: Message[] = [];
      messages.value = {
        ...messages.value,
        [domainId]: { ...messages.value[domainId], [topicId]: topicMessages },
      };
    }

    return messages.value[domainId][topicId];
  };

  /** 追加もしくは更新を行う */
  const addOrUpdate = (
    list: Message[],
    domainId: string,
    topicId: string,
  ): void => {
    list.forEach((message) => {
      const topicMessages = findTopicMessages(domainId, topicId);
      const index = topicMessages.findIndex((m) => m.id === message.id);
      if (index < 0) {
        topicMessages.push(message);
      } else {
        topicMessages.splice(index, 1, message);
      }
      // ソート
      topicMessages.sort((l, r) => {
        return l.updatedAt.getTime() > r.updatedAt.getTime() ? -1 : 1;
      });
    });
  };

  // --- getters ---
  const get = (
    domainId: string,
    topicId: string,
    exists = false,
  ): Message[] | undefined => {
    const domainTopicList = messages.value[domainId];
    if (!domainTopicList) return undefined;
    const list = domainTopicList[topicId];
    if (!list) return undefined;
    return exists ? list.filter((m) => Message.isExists(m)) : list;
  };

  const getNextToken = (topicId: string): string | null | undefined => {
    return nextToken.value[topicId];
  };

  const getRange = (topicId: string): DateRange => {
    return range.value[topicId];
  };

  // --- 内部更新 (旧 mutations 相当) ---
  const addMutation = ({
    messages: list,
    topicId,
    domainId,
  }: PayloadAdd): void => {
    addOrUpdate(list, domainId, topicId);
  };

  const updateMutation = ({ message }: Payload): void => {
    addOrUpdate([message], message.domainId, message.topicId);
  };

  const resetMutation = ({
    domainId,
    topicId,
    messageId,
  }: {
    domainId: string;
    topicId: string;
    messageId: string;
  }): void => {
    const list = findTopicMessages(domainId, topicId);
    const index = list.findIndex((m) => m.id === messageId);
    // 1つだけの時のみ
    if (index > -1 && list.length === 1) {
      const { [topicId]: _, ...restTopics } = messages.value[domainId];
      messages.value = { ...messages.value, [domainId]: restTopics };
      nextToken.value[topicId] = undefined;
    }
  };

  // --- actions ---
  const reset = async (envelope: {
    domainId: string;
    topicId: string;
    messageId: string;
  }): Promise<void> => {
    resetMutation(envelope);
  };

  const create = async (envelope: {
    message: Message;
    topicId?: string;
    domainId?: string;
  }): Promise<void> => {
    const app = useAppStore();
    const commentStore = useCommentStore();
    const message = envelope.message;
    message.setDomainAndTopicIfNotSet(
      envelope.domainId || app.domainId,
      envelope.topicId || app.topicId,
    );
    const domainId = message.domainId;
    const topicId = message.topicId;

    // commentが用意されていなかったら、今の段階で場所を用意しておく
    const comments = commentStore.get(message.id) as unknown as Comments[];
    if (comments === undefined) {
      await commentStore.add({ comments: [], messageId: envelope.message.id });
    }

    // DBに保存
    const register = await MessageDao.create(message);
    if (register) {
      const newMessage = register.message;
      addMutation({ messages: [newMessage], domainId, topicId });
      const thumbs = register.thumbs;
      const data = register.data;
      useFileStore().set({ thumb: thumbs, data: data });

      const access = new ServerApiAccess();

      // Topicの更新(投稿数)依頼
      const updateMessageCount = true;
      await access.putTopicMessageCount(domainId, topicId, updateMessageCount);
    }
  };

  const update = async (envelope: {
    messageId: string;
    param: Partial<Message>;
    topicId?: string;
    domainId?: string;
  }): Promise<void> => {
    const app = useAppStore();
    const commentStore = useCommentStore();
    const messageId = envelope.messageId;
    const domainId = app.domainId;
    const topicId = app.topicId;

    const param = envelope.param;
    param.comments = commentStore.get(messageId); // コメントを追加
    const messageObject: Partial<Message> = {
      ...param,
      id: messageId,
      domainId,
      topicId,
    };
    const message = Message.create(messageObject);

    const register = await MessageDao.update(message);
    if (register) {
      const newMessage = register.message;
      updateMutation({ message: newMessage });
      const thumbs = register.thumbs;
      const data = register.data;
      useFileStore().set({ thumb: thumbs, data: data });

      const access = new ServerApiAccess();

      // Topicの更新依頼
      // TODO: update-message API側に統合する
      const updateMessageCount = !!param.deleted; // 削除時は投稿数を更新、更新時は話題更新日時のみ更新する
      await access.putTopicMessageCount(domainId, topicId, updateMessageCount);
    }

    if (param.deleted && param.deletedUser) {
      const comments = commentStore.get(topicId, messageId);
      if (!comments) return;

      comments.forEach(async (comment: Partial<Comment>) => {
        const commentId = comment.id as string;
        if (!comment.deleted) {
          const payload: CommentUpdatePayload = {
            commentId: commentId,
            messageId: messageId,
            param: { deleted: param.deleted, deletedUser: param.deletedUser },
          };
          await commentStore.update(payload);
        }
      });
    }
  };

  /** DBから投稿データをロードして Store に保存する */
  const fetch = async ({
    domainId,
    topicId,
    messageId,
  }: PayloadFetch): Promise<void> => {
    // MessageDao.read は (messageId) => Comment[] のシグネチャを期待するが、
    // 実体は comment-store の get(topicId, messageId?) を 1 引数で呼ぶ既存挙動を踏襲。
    // 戻り値の undefined は MessageDao 側の `|| []` で吸収されるため、型のみキャストで合わせる。
    const getCommentFunc = useCommentStore().get as (
      messageId: string,
    ) => Comment[];
    const messageResults = await MessageDao.read(
      domainId,
      topicId,
      getCommentFunc,
      messageId,
    );
    if (messageResults) {
      const list = messageResults.messages;
      addMutation({ messages: list, topicId, domainId });
    }
  };

  /** Subscriptionを登録する */
  const registerSubscription = async ({
    domainId,
    topicId,
  }: PayloadRegisterSubscription): Promise<void> => {
    await MessageDao.registerSubscrptions(domainId, topicId, {
      onCreate: (domainId: string, topicId: string, messageId: string) => {
        fetch({ domainId, topicId, messageId });
      },
      onUpdate: (domainId: string, topicId: string, messageId: string) => {
        fetch({ domainId, topicId, messageId });
      },
      onDelete: () => {
        // 注: 旧 Vuex 実装は存在しない 'delete' mutation を commit しており
        // 実質的に no-op だったため、挙動を踏襲して何もしない
      },
    });
  };

  /** Subscriptionを登録解除 */
  const removeSubscription = async (payload: {
    topicId: string;
  }): Promise<void> => {
    await MessageDao.closeSubscription(payload.topicId);
  };

  return {
    messages,
    nextToken,
    range,
    get,
    getNextToken,
    getRange,
    reset,
    create,
    update,
    fetch,
    registerSubscription,
    removeSubscription,
  };
});

export default useMessageStore;
